import { message } from 'antd'
import { fetchUtil as fetch, buildQuery } from 'src/utils/fetch'
import { omit } from 'ramda'
import { saveAuditData } from 'src/api/audit'

export const getAll = async (params: Record<string, any>) => {
  const query = buildQuery(params)
  try {
    const { data } = await fetch(`/playlist/tags/?${query}`)
    return data
  } catch (e) {
    message.error('Tag getAll error')
  }
}

export const search = async (params) => {
  try {
    const query = buildQuery(params)
    const { data } = await fetch(`/playlist/tags/search/?${query}`)
    return data
  } catch (e) {
    message.error('Tag search error')
  }
}

export const getById = async (id) => {
  try {
    const { data } = await fetch(`/playlist/tag/${id}`)
    return data
  } catch (e) {
    message.error('Tag getById error')
  }
}

export const create = async (data) => {
  try {
    const body = new FormData()
    Object.entries(data)
      .filter(([name]) => ['image', 'name', 'type'].includes(name))
      .forEach(([name, value]: any) => {
        if (!value) return
        if (name === 'image') {
          body.append(name, value.file, value.file.name)
        } else {
          body.append(name, value)
        }
      })
    const response = await fetch(`/playlist/tag/`, {
      method: 'POST',
      body,
    })
    saveAuditData('CREATE', 'TAG', response.data.id)
    return response
  } catch (e) {
    message.error('Tag create error')
  }
}

export const deleteTag = async (id) => {
  try {
    const response = await fetch(`/playlist/tag/${id}`, {
      method: 'DELETE',
    })
    saveAuditData('CREATE', 'TAG', response.data.id)
    return response
  } catch (e) {
    message.error('Tag deleteTag error')
  }
}

export const fetchAllByGroup = async () => {
  try {
    const { data } = await getAll({
      page: 0,
      size: 1000,
    })
    return data?.reduce(
      (data, current) => {
        if (current.type === 'GENRE') {
          data.GENRE.push(current)
        } else if (current.type === 'ACTIVITY') {
          data.ACTIVITY.push(current)
        } else {
          data.MOOD.push(current)
        }
        return data
      },
      {
        GENRE: [],
        ACTIVITY: [],
        MOOD: [],
      }
    )
  } catch (e) {
    message.error('fetchAllByGroup error')
  }
}

export const updateOrCreate = async (
  id: string,
  payload: Record<string, any>
) => {
  try {
    let res = null
    if (id && id !== 'new') {
      res = await fetch(`/playlist/tag/${id}`, {
        method: 'PUT',
        body: JSON.stringify(omit(['image'], payload)),
      })
      saveAuditData('EDIT', 'TAG', res.data.name)
    } else {
      res = await create(payload)
    }
    return res
  } catch (e) {
    message.error('updateOrCreate error !')
  }
}

export const uploadImage = async (id: string, file: any) => {
  try {
    const body = new FormData()
    body.append('image', file, file.name)
    return await fetch(`/playlist/tag/${id}/image`, {
      method: 'PUT',
      body,
    })
  } catch (e) {
    message.error('uploadImage error !')
  }
}

export const getAllGameTags = async (page = 0, size = 10, name, category) => {
  try {
    const requestBody: {
      page: number
      size: number
      name?: string
      category?: string
    } = {
      page,
      size,
    }

    if (name) {
      requestBody.name = name
    }

    if (category) {
      requestBody.category = category
    }

    const { data } = await fetch(`/metaverse-rooms/tags/all`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        role: 'ADMIN',
      },
    })

    return data
  } catch (e) {
    message.error('Game Tag getAll error')
    throw e
  }
}

export const getGameTagsCategory = async () => {
  try {
    const { data } = await fetch(`/metaverse-rooms/tags/category`)
    return data
  } catch (e) {
    message.error('Game Tag category error')
    throw e
  }
}

export const getAllGamesByTag = async (page = 0, size = 10, id) => {
  try {
    const { data } = await fetch(
      `/metaverse-rooms/tags/games/${id}?size=${size}&page=${page}`
    )
    return data
  } catch (e) {
    message.error('Game Tag getAllGamesByTag error')
    throw e
  }
}

export const deleteGameByTag = async (id, gameId) => {
  try {
    const { data } = await fetch(`/metaverse-rooms/tags/${id}/${gameId}`, {
      method: 'DELETE',
      headers: {
        role: 'ADMIN',
      },
    })

    return data
  } catch (e) {
    message.error('Game Tag deleteGameByTag error')
    throw e
  }
}

export const addGameByTag = async (id, gameId) => {
  try {
    const { data } = await fetch(`/metaverse-rooms/tags/${id}/${gameId}`, {
      method: 'PATCH',
      headers: {
        role: 'ADMIN',
      },
    })

    return data
  } catch (e) {
    message.error('Game Tag addGameByTag error')
    throw e
  }
}

export const editGameTag = async (id, nameEn, nameAr, nameFr, category) => {
  const requestBody: {
    id: string
    nameEn?: number
    nameAr?: string
    nameFr?: string
    category?: string
  } = {
    id,
  }

  if (nameEn) {
    requestBody.nameEn = nameEn
  }

  if (nameAr) {
    requestBody.nameAr = nameAr
  }
  if (nameFr) {
    requestBody.nameFr = nameFr
  }
  if (category) {
    requestBody.category = category
  }

  await fetch(`/metaverse-rooms/tags/edit`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      role: 'ADMIN',
    },
  })
}

export const addGamesByTag = async (gameIds: string[], tagId) => {
  const requestBody = {
    ids: gameIds,
  }

  try {
    await fetch(`/metaverse-rooms/tags/games/${tagId}`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        role: 'ADMIN',
      },
    })
  } catch (e) {
    message.error('Game Tag addGamesByTag error')
    throw e
  }
}

export const addTagsByGame = async (tagIds: string[], gameId) => {
  const requestBody = {
    ids: tagIds,
  }

  try {
    await fetch(`/metaverse-rooms/tags/${gameId}`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        role: 'ADMIN',
      },
    })
  } catch (e) {
    message.error('Game Tag addTagsByGame error')
    throw e
  }
}

export const addGameTag = async (nameEn, category, nameAr, nameFr) => {
  const requestBody: {
    nameEn: number
    category: string
    nameAr?: string
    nameFr?: string
  } = {
    nameEn,
    category,
  }

  if (nameAr) {
    requestBody.nameAr = nameAr
  }
  if (nameFr) {
    requestBody.nameFr = nameFr
  }

  await fetch(`/metaverse-rooms/tags/add`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      role: 'ADMIN',
    },
  })
}

export const getTagsByGameId = async (gameId, page = 0, size = 10) => {
  try {
    const { data } = await fetch(
      `/metaverse-rooms/tags/${gameId}?page=${page}&size=${size}`
    )
    return data
  } catch (e) {
    message.error('Game Tag getTagsByGameId error')
    throw e
  }
}
